/**
 * Historical benchmark: the lunch schedule a manager produced by hand for the
 * week commencing 27 July 2026.
 *
 * This data is used in three places and **never** to short-circuit the
 * scheduler:
 *
 *  1. `docs/RULES.md` documents the rules reverse-engineered from it.
 *  2. The optional `history` scoring term nudges the solver towards times this
 *     team actually uses (a small bonus - it cannot override a hard rule).
 *  3. `tests/benchmark.test.js` asserts that Lunchly reaches the same
 *     *decisions* (who eats, who does not, day vs evening) without demanding
 *     identical clock times.
 *
 * Known anomalies in the source data are recorded in ANOMALIES below rather
 * than being quietly "fixed", because they are exactly the cases the manual
 * override feature exists to serve.
 */

/** Raw historical lunches, keyed by day. Names are the short forms managers use. */
export const BENCHMARK_LUNCHES = {
  mon: [
    { time: '12:00', names: ['Jill'] },
    { time: '12:30', names: ['Josh'] },
    { time: '13:00', names: ['Emma', 'Alicia'] },
    { time: '13:30', names: ['Grace', 'Cathy'] },
    { time: '14:00', names: ['Neil', 'Rona'] },
    { time: '14:30', names: ['Cameron', 'George'] },
    { time: '15:00', names: ['Finn'] },
    { time: '16:00', names: ['Clarissa'] },
    { time: '18:30', names: ['William'] },
    { time: '19:00', names: ['Jonny'] },
    { time: '19:30', names: ['Jack'] },
  ],
  tue: [
    { time: '12:00', names: ['Jill'] },
    { time: '12:30', names: ['Alicia'] },
    { time: '13:00', names: ['Glen'] },
    { time: '13:30', names: ['Rona'] },
    { time: '14:00', names: ['Neil'] },
    { time: '14:30', names: ['Cathy'] },
    { time: '15:00', names: ['Josh'] },
    { time: '15:30', names: ['Ronan'] },
    { time: '16:00', names: ['Grace'] },
    { time: '19:00', names: ['George'] },
    { time: '19:30', names: ['Luke'] },
    { time: '20:00', names: ['Ryan'] },
  ],
  wed: [
    { time: '12:00', names: ['Jill'] },
    { time: '12:30', names: ['Emma'] },
    { time: '13:00', names: ['Glen', 'Rona'] },
    { time: '13:30', names: ['Finn'] },
    { time: '14:00', names: ['Neil'] },
    { time: '14:30', names: ['Cags'] },
    { time: '15:00', names: ['George'] },
    { time: '15:30', names: ['Tanya'] },
    { time: '16:00', names: ['Clarissa'] },
    { time: '19:00', names: ['Luke'] },
    { time: '19:30', names: ['William'] },
    { time: '20:00', names: ['Heather'] },
  ],
  thu: [
    { time: '12:00', names: ['Emma'] },
    { time: '12:30', names: ['Cathy'] },
    { time: '13:00', names: ['Josh'] },
    { time: '13:30', names: ['George'] },
    { time: '14:00', names: ['Leon'] },
    { time: '14:30', names: ['Ronan'] },
    { time: '15:00', names: ['Neil'] },
    { time: '15:30', names: ['Tanya'] },
    { time: '16:00', names: ['Grace'] },
    { time: '19:00', names: ['William'] },
    { time: '19:30', names: ['Jack'] },
    { time: '20:00', names: ['Heather'] },
  ],
  fri: [
    { time: '12:00', names: ['Glen'] },
    { time: '12:30', names: ['Jill'] },
    { time: '13:00', names: ['Cathy'] },
    { time: '14:00', names: ['Josh'] },
    { time: '15:00', names: ['Ryan'] },
    { time: '16:00', names: ['Grace'] },
    { time: '19:00', names: ['Cameron'] },
    { time: '19:30', names: ['Luke'] },
    { time: '20:00', names: ['Jonny'] },
  ],
  sat: [
    { time: '12:00', names: ['Cathy'] },
    { time: '12:30', names: ['Alicia'] },
    { time: '13:00', names: ['Emma'] },
    { time: '13:30', names: ['Josh'] },
    { time: '14:00', names: ['Neil'] },
    { time: '14:30', names: ['Cameron'] },
    { time: '15:00', names: ['Rebekah'] },
    { time: '18:00', names: ['Ronan'] },
    { time: '18:30', names: ['Jack'] },
    { time: '19:00', names: ['William'] },
  ],
  // No historical schedule was supplied for Sunday 2 August 2026.
  sun: [],
};

/**
 * Documented oddities in the historical week. Each one is a legitimate manual
 * decision, not a rule - Lunchly reproduces this behaviour through overrides,
 * not by changing the algorithm.
 */
export const BENCHMARK_ANOMALIES = [
  {
    dayKey: 'tue',
    subject: 'Grace',
    note:
      'Grace Valentine is not rostered on Tuesday, yet appears at 16:00. Clarissa Adair '
      + '(13:00-21:00) is rostered and absent from the list. Most likely a transcription slip '
      + 'or a same-day swap.',
  },
  {
    dayKey: 'thu',
    subject: 'Glenda Black',
    note:
      'Glenda works 07:00-14:00 (7 hrs) but receives no lunch. With one lunch per slot the '
      + 'early slots were taken by Emma and Cathy, and anything from 13:00 leaves under an '
      + 'hour of her shift. Lunchly raises an "eligible but unassigned" warning here.',
  },
  {
    dayKey: 'fri',
    subject: 'Josh Maunder',
    note:
      'Josh starts at 13:00 and takes lunch at 14:00 - only one hour in. This is the "lunch '
      + 'unusually early" case: valid operationally, impossible under the default '
      + '"earliest lunch after shift start" rule, and therefore a manual override.',
  },
  {
    dayKey: 'fri',
    subject: 'Rona Bryson',
    note: 'Rona works 08:30-16:30 but receives no lunch on the quieter Friday rota.',
  },
  {
    dayKey: 'all',
    subject: 'Roma McCausland',
    note:
      'Roma works 06:30-16:00 every weekday (47h30) and never appears in any lunch list. '
      + 'Modelled as a seeded staff rule with excludeFromLunch: true rather than as a '
      + 'scheduler special case.',
  },
];

/**
 * Distinct clock times the team historically uses, split by group. Feeds the
 * optional `history` scoring term.
 */
export const HISTORICAL_DAY_SLOTS = ['12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00'];
export const HISTORICAL_EVENING_SLOTS = ['18:00', '18:30', '19:00', '19:30', '20:00'];

/** Flatten the benchmark into `{ dayKey, time, name }` triples. */
export function benchmarkEntries() {
  const entries = [];
  for (const [dayKey, slots] of Object.entries(BENCHMARK_LUNCHES)) {
    for (const slot of slots) {
      for (const name of slot.names) {
        entries.push({ dayKey, time: slot.time, name });
      }
    }
  }
  return entries;
}

/** Short names that received a lunch on a given day. */
export function benchmarkNamesForDay(dayKey) {
  return (BENCHMARK_LUNCHES[dayKey] ?? []).flatMap((slot) => slot.names);
}
